/**
 * Shared market lifecycle status helpers.
 *
 * On-chain storage can stay `Open` past close_block until a write flips it;
 * reads (after 0.2.0+) and the UI treat that as effective `pending_no`.
 */

export const STATUS_ORDER = [
  "open",
  "pending_yes",
  "pending_no",
  "under_review",
  "finalized",
  "cancelled",
];

// The contract's `MarketStatus` enum (call_types.rs) has no
// `#[serde(rename_all = ...)]`, so the data-driver/gateway JSON carries
// the exact Rust variant spelling - PascalCase, multi-word variants with
// no separator ("PendingYes", "UnderReview"). Naively lowercasing that
// ("pendingyes") never matches STATUS_ORDER's snake_case entries
// ("pending_yes"), so `STATUS_ORDER.indexOf(...)` silently returns -1 for
// every status except the single-word ones ("Open"/"Finalized"/
// "Cancelled" happen to survive a bare lowercase) - which is exactly why
// the lifecycle bar never lit up the pending phases. Map explicitly
// instead of guessing a casing transform.
const STATUS_ALIASES = {
  open: "open",
  pendingyes: "pending_yes",
  pending_yes: "pending_yes",
  pendingno: "pending_no",
  pending_no: "pending_no",
  underreview: "under_review",
  under_review: "under_review",
  finalized: "finalized",
  cancelled: "cancelled",
  canceled: "cancelled",
};

export function normalizeStatus(statusRaw) {
  if (statusRaw == null) return "open";
  if (typeof statusRaw === "string") {
    const s = statusRaw.toLowerCase().replace(/[\s-]+/g, "");
    // Numeric enums sometimes arrive as strings.
    if (/^\d+$/.test(s)) return STATUS_ORDER[Number(s)] ?? "open";
    // Strip underscores for alias lookup too (Pending_Yes → pendingyes).
    const compact = s.replace(/_/g, "");
    const aliased = STATUS_ALIASES[s] ?? STATUS_ALIASES[compact] ?? s;
    return STATUS_ORDER.includes(aliased) ? aliased : "open";
  }
  if (typeof statusRaw === "number") return STATUS_ORDER[statusRaw] ?? "open";
  return "open";
}

/**
 * @param {string|number} status
 * @param {{ closeBlock?: number|null, blockHeight?: number|null }} [opts]
 */
export function effectiveStatus(status, { closeBlock, blockHeight } = {}) {
  const base = normalizeStatus(status);
  if (base !== "open") return base;
  if (closeBlock == null || blockHeight == null) return base;
  if (Number(blockHeight) >= Number(closeBlock)) return "pending_no";
  return base;
}

export function effectiveResolution(resolution, status, { closeBlock } = {}) {
  const out = resolution ? { ...resolution } : {};
  out.status = status;
  if (status === "pending_no" && out.pending_outcome == null) {
    out.pending_outcome = 1;
    out.window_started_at_block = out.window_started_at_block ?? closeBlock ?? null;
  }
  return out;
}

export function isLiveStatus(status) {
  const s = normalizeStatus(status);
  return (
    s === "open" ||
    s === "pending_yes" ||
    s === "pending_no" ||
    s === "under_review"
  );
}

export function isResolvedStatus(status) {
  const s = normalizeStatus(status);
  return s === "finalized" || s === "cancelled";
}

export function statusBadgeVariant(status) {
  const s = normalizeStatus(status);
  if (s === "under_review") return "danger";
  if (s === "finalized") return "success";
  if (s === "cancelled") return "neutral";
  return "pending";
}

/** Human-readable phase name, keyed by `STATUS_ORDER` entry. */
export const STATUS_LABELS = {
  open: "Open",
  pending_yes: "Pending YES",
  pending_no: "Pending NO",
  under_review: "Under review · council",
  finalized: "Finalized",
  cancelled: "Cancelled",
};

/** Plain-language explanation of what each phase means, for the phase
 * detail panel (market-detail.js) - shown regardless of whether this
 * market ever actually reached that phase, so a viewer can understand a
 * step they're browsing back to as much as the live one. */
export const PHASE_DESCRIPTIONS = {
  open: "Trading is open - anyone can buy or sell YES/NO shares against the pool. Before close, the owner may assert YES. Otherwise, once trading closes the market moves to Pending NO by default.",
  pending_yes: "The owner asserted YES. Anyone may bond-dispute this during the challenge window. If unchallenged when the window ends, it finalizes as YES.",
  pending_no: "Trading closed without an assertion, so the market defaults toward NO. The owner may still bond-challenge this (a “late YES”) during the challenge window.",
  under_review:
    "A dispute or late challenge is under review. Dispute-council members resolve via local multisig-tool (CLI / pm-resolve demo) - see the Council action panel. If the review window times out first, the pending outcome stands. Owner portal Resolve is an escape hatch only.",
  finalized: "Resolution is final. Winning positions can be claimed from Portfolio.",
  cancelled: "This event was cancelled. Deposited collateral can be redeemed back from Portfolio.",
};

export function outcomeLabel(outcome) {
  if (outcome === 0 || outcome === "yes" || outcome === "YES") return "YES";
  if (outcome === 1 || outcome === "no" || outcome === "NO") return "NO";
  return "-";
}

/** Truncates a hex string, or a byte array (rendered as hex first), for
 * compact display - same shape the gateway/data-driver returns fields
 * like `dispute.disputer`/`evidence_hash` in. */
export function truncHex(v, n = 10) {
  if (v == null) return "-";
  const s =
    typeof v === "string"
      ? v
      : Array.isArray(v)
        ? Array.from(v, (b) => b.toString(16).padStart(2, "0")).join("")
        : String(v);
  if (s.length <= n + 1) return s;
  return `${s.slice(0, n)}…`;
}

/**
 * Extracts the fields worth showing for a market's on-chain `resolution`
 * object as a flat, order-preserving list - the single source of what
 * resolution data exists, shared between `resolution-panel.js`'s action
 * card and `market-detail.js`'s phase detail panel so both read from the
 * same shape instead of each hand-picking fields (and drifting).
 *
 * Returns `{ key, label, value }` rows where `value` is a small tagged
 * union (`{kind: "outcome"|"dusk"|"hex"|"text", ...}`) - deliberately not
 * DOM nodes, since the two callers render with different helpers
 * (`duskAmountNode` vs. plain text, etc.).
 *
 * @param {object|null} resolution
 * @returns {Array<{key: string, label: string, value: object}>}
 */
export function resolutionSummary(resolution) {
  if (!resolution) return [];
  const rows = [];
  if (resolution.pending_outcome != null) {
    rows.push({ key: "pending", label: "Pending", value: { kind: "outcome", outcome: resolution.pending_outcome } });
  }
  if (resolution.window_started_at_block != null) {
    rows.push({
      key: "window_start",
      label: "Window start",
      value: { kind: "text", text: String(resolution.window_started_at_block) },
    });
  }
  if (resolution.challenge_window_blocks != null) {
    rows.push({
      key: "challenge_window",
      label: "Challenge window",
      value: { kind: "text", text: `${resolution.challenge_window_blocks} blocks` },
    });
  }
  if (resolution.review_window_blocks != null) {
    rows.push({
      key: "review_window",
      label: "Review window",
      value: { kind: "text", text: `${resolution.review_window_blocks} blocks` },
    });
  }
  const dispute = resolution.dispute;
  if (dispute && (dispute.claimed_outcome != null || dispute.bond != null)) {
    rows.push({ key: "claimed", label: "Claimed", value: { kind: "outcome", outcome: dispute.claimed_outcome } });
    rows.push({
      key: "bond",
      label: "Bond",
      value: dispute.bond != null ? { kind: "dusk", lux: dispute.bond } : { kind: "text", text: "-" },
    });
    rows.push({ key: "disputer", label: "Disputer", value: { kind: "hex", hex: dispute.disputer } });
    rows.push({ key: "evidence", label: "Evidence", value: { kind: "hex", hex: dispute.evidence_hash } });
  }
  if (resolution.winning_outcome != null) {
    rows.push({ key: "winner", label: "Winner", value: { kind: "outcome", outcome: resolution.winning_outcome } });
  }
  return rows;
}

/**
 * Blocks remaining in the active challenge or review window.
 * @returns {{ kind: "challenge"|"review", remaining: number, total: number } | null}
 */
export function windowBlocksRemaining(resolution, status, blockHeight) {
  if (resolution == null || blockHeight == null) return null;
  const start = resolution.window_started_at_block;
  if (start == null) return null;
  const s = normalizeStatus(status);
  let total = null;
  let kind = null;
  if (s === "pending_yes" || s === "pending_no") {
    total = resolution.challenge_window_blocks;
    kind = "challenge";
  } else if (s === "under_review") {
    total = resolution.review_window_blocks;
    kind = "review";
  }
  if (total == null || kind == null) return null;
  const end = Number(start) + Number(total);
  const remaining = end - Number(blockHeight);
  return { kind, remaining, total: Number(total), endBlock: end };
}
